import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { WithContext } from "schema-dts";

import { fetchCourses } from "_components/CourseCardList/getCourseCards";
import { fetchCoveoEvents } from "_components/CourseCardList/getCoveoEvents";
import { TAX_RULES, calcTaxInclusivePriceSafe } from "_components/CourseStickyPanel/helper";
import { DEFAULT_COUNTRY_NAME } from "_containers/CountryContext";
import { getLink } from "_utils/helpers/link";

type RouteComponent = {
  componentName?: string;
  dataSource?: string;
  uid?: string;
  id?: string;
  fields?: Record<string, any>;
  placeholders?: Record<string, RouteComponent[]>;
};

type CountryOption = {
  id?: string;
  name: string;
};

type ListingItem = {
  id?: string;
  type?: string;
  title?: string | { jss?: { value?: string }; value?: string };
  startDate?: string;
  schemaStartDate?: string;
  cpdHours?: string | null;
  link?: unknown;
  url?: unknown | { jss?: { value?: string }; value?: string };
  href?: unknown;
  clickUri?: string;
  uri?: string;
  category?: string;
  tags?: Array<{ id?: string; displayName?: string }>;
  courseReason?: string | string[];
  pageTitle?: { jss?: { value?: string } };
  marketingTitle?: { jss?: { value?: string } };
  format?: { jss?: { value?: string } } | string | null;
  oStartDate?: { jss?: { value?: string } };
  cPDhours?: { jss?: { value?: string } };
  rating?: { jss?: { value?: string } } | string | null;
  children?: any[];
  countryPrices?: Array<{
    regionKey?: string;
    memberPrice?: string;
    nonMemberPrice?: string;
    fSPrice?: string;
    currencyCode?: string;
  }>;
  courseMemberPrice?: string;
  courseNonMemberPrice?: string;
  courseCurrencyCode?: string;
  coursePriceType?: string;
  schemaCountryName?: string;
};

type ItemLocation = { "@type": "Country"; name: string } | { "@type": "VirtualLocation" };

type SchemaOffer = {
  "@type": "Offer";
  price?: string;
  priceCurrency?: string;
  eligibleRegion?: { "@type": "Country"; name: string };
};

type ItemListEntity = {
  "@type": "ItemList";
  name: string;
  itemListElement: Array<{
    "@type": "ListItem";
    position: number;
    item: {
      "@type": "Course" | "Event";
      name: string;
      url?: string;
      startDate?: string;
      location?: ItemLocation;
      organizer?: { "@id": string };
      offers?: SchemaOffer[];
    };
  }>;
};

type ComponentEntity =
  | ItemListEntity
  | {
      "@type": "WebPageElement";
      name: string;
      mainEntity: ItemListEntity[];
    };

const COUNTRY_COMPONENT_NAMES = ["CountryCourseSelector", "CountryPreferenceSelector"];
const PAGE_BANNER_COMPONENT_NAMES = ["PageBanner", "HeroBanner"];
const LISTING_COMPONENT_NAMES = ["TrendingCardList", "CourseCardList", "CurrentCardContainer", "CareerStageLearning"];
const COURSE_TOPIC_PAGE_TEMPLATE_ID = "64b82609-c0cb-48f8-acbf-93c440317819";
const AUSTRALIA_COUNTRY: CountryOption = { id: "au", name: "Australia" };
const MAX_SCHEMA_ITEM_LIST_ITEMS = 9;
const DEFAULT_TRENDING_NUM_CARDS = 9;
const CPA_ORGANIZATION_ID = "https://www.cpaaustralia.com.au/#organization";

const stripTags = (value?: string) => (value ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

function getJssOrStringValue(value?: string | { jss?: { value?: string } } | null) {
  if (typeof value === "string") {
    return value;
  }

  return value?.jss?.value || "";
}

function getTextValue(value: any): string {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value?.value === "string") {
    return value.value;
  }

  if (typeof value?.jss?.value === "string") {
    return value.jss.value;
  }

  return "";
}

function toSchemaStartDate(value?: string) {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  const sitecoreDate = moment.utc(
    trimmedValue,
    ["YYYYMMDD[T]HHmmss[Z]", "YYYYMMDD[T]HHmmss"],
    true
  );

  if (sitecoreDate.isValid()) {
    return sitecoreDate.toISOString();
  }

  const displayDate = moment.parseZone(
    trimmedValue,
    [
      moment.ISO_8601,
      "D MMM YYYY",
      "D MMMM YYYY",
      "DD MMM YYYY",
      "DD MMMM YYYY",
      "D/M/YYYY",
      "DD/MM/YYYY",
      "YYYY-MM-DD",
    ],
    true
  );

  return displayDate.isValid() ? displayDate.toISOString() : undefined;
}

function normalizeCountryName(countryName?: string) {
  return (countryName ?? "").trim().toLowerCase();
}

function isAustraliaCountry(country: CountryOption) {
  return (
    country?.id?.toLowerCase() === AUSTRALIA_COUNTRY.id ||
    normalizeCountryName(country?.name) === normalizeCountryName(AUSTRALIA_COUNTRY.name)
  );
}

function getSiteBaseUrl(context: any) {
  if (context?.siteInfo?.siteBaseUrl) {
    return `https${context.siteInfo.siteBaseUrl}`;
  }

  try {
    return new URL(context?.canonicalUrl ?? "").origin;
  } catch {
    return "";
  }
}

function toAbsoluteUrl(baseUrl: string, value?: string) {
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (!baseUrl) {
    return value;
  }

  return `${baseUrl}${value.startsWith("/") ? "" : "/"}${value}`;
}

function resolveLinkValue(baseUrl: string, value: any) {
  const sources = [
    value,
    value?.link,
    value?.link?.value,
    value?.link?.jss?.value,
    value?.url?.jss?.value,
    value?.url?.value,
    value?.jss?.value,
    value?.value,
    value?.fields?.link?.value,
    value?.fields?.link?.jss?.value,
    value?.fields?.url?.value,
    value?.children?.[0],
    value?.route,
    value?.route?.fields?.link?.value,
  ];

  for (const source of sources) {
    if (!source) {
      continue;
    }

    const normalizedLink = getLink(source);
    const candidate =
      normalizedLink?.url ||
      normalizedLink?.href ||
      normalizedLink?.to ||
      (typeof source === "string" ? source : undefined) ||
      (typeof source?.href === "string" ? source.href : undefined) ||
      (typeof source?.url === "string" ? source.url : undefined) ||
      (typeof source?.path === "string" ? source.path : undefined) ||
      (typeof source?.clickUri === "string" ? source.clickUri : undefined) ||
      (typeof source?.uri === "string" ? source.uri : undefined);

    if (typeof candidate !== "string") {
      continue;
    }

    const trimmedCandidate = candidate.trim();
    if (!trimmedCandidate || trimmedCandidate === "#" || /^javascript:/i.test(trimmedCandidate)) {
      continue;
    }

    return toAbsoluteUrl(baseUrl, trimmedCandidate);
  }

  return undefined;
}

const VIRTUAL_KEYWORDS = ["online", "virtual", "self-paced", "selfpaced", "webinar", "on-demand", "on demand"];

function isVirtualListingItem(item: ListingItem): boolean {
  const category = (item.category || "").toLowerCase();
  const format =
    typeof item.format === "string"
      ? item.format.toLowerCase()
      : (item.format?.jss?.value || "").toLowerCase();
  return VIRTUAL_KEYWORDS.some((kw) => category.includes(kw) || format.includes(kw));
}

function buildItemLocation(country: CountryOption, item: ListingItem): ItemLocation | undefined {
  if (item.type !== "event") {
    return undefined;
  }
  if (isVirtualListingItem(item)) {
    return { "@type": "VirtualLocation" };
  }
  if (item.schemaCountryName) {
    return { "@type": "Country", name: item.schemaCountryName };
  }
  if (country.name) {
    return { "@type": "Country", name: country.name };
  }
  return undefined;
}

function collectComponents(root: RouteComponent | RouteComponent[] | null | undefined, names: string[]) {
  const matches: RouteComponent[] = [];

  const visit = (node: RouteComponent | RouteComponent[] | null | undefined) => {
    if (!node) {
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (node.componentName && names.includes(node.componentName)) {
      matches.push(node);
    }

    const placeholders = node.placeholders;
    if (!placeholders || typeof placeholders !== "object") {
      return;
    }

    Object.values(placeholders).forEach(visit);
  };

  visit(root);

  return matches;
}

function dedupeCountries(countries: CountryOption[]) {
  const seen = new Set<string>();

  return countries.filter((country) => {
    const key = normalizeCountryName(country?.name);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function getCountryOptions(route: any) {
  const countryComponents = collectComponents(route, COUNTRY_COMPONENT_NAMES);

  const countries = countryComponents
    .map((component) => {
      const countriesData = component?.fields?.data?.countries;
      const countryCourseSelectorItems = countriesData?.countries;
      if (Array.isArray(countryCourseSelectorItems) && countryCourseSelectorItems.length > 0) {
        return dedupeCountries(
          countryCourseSelectorItems
            .map((item: any) => ({
              id: item?.code,
              name: item?.courseRegion?.value,
            }))
            .filter((item: CountryOption) => !!item?.name)
        );
      }

      const dropdownItems = countriesData?.countryDropdownData?.targetItems;
      if (Array.isArray(dropdownItems) && dropdownItems.length > 0) {
        return dedupeCountries(
          dropdownItems
            .map((item: any) => ({
              id: item?.id,
              name: item?.name?.jss?.value || item?.name?.value,
            }))
            .filter((item: CountryOption) => !!item?.name)
        );
      }

      return [];
    })
    .find((items) => items.length > 0);

  if (countries && countries.length > 0) {
    return countries;
  }

  return [{ name: DEFAULT_COUNTRY_NAME }];
}

function getDefaultCountry(countries: CountryOption[]) {
  return (
    countries.find(
      (country) => normalizeCountryName(country?.name) === normalizeCountryName(DEFAULT_COUNTRY_NAME)
    ) || countries[0] || { name: DEFAULT_COUNTRY_NAME }
  );
}

function getPageBannerText(route: any) {
  const pageBanner = collectComponents(route, PAGE_BANNER_COMPONENT_NAMES)[0];

  if (pageBanner?.componentName === "HeroBanner") {
    const datasource = pageBanner?.fields?.data?.datasource;
    return {
      name: getTextValue(datasource?.title),
      description: getTextValue(datasource?.subTitle),
    };
  }

  const pageFields = pageBanner?.fields?.data?.pageFields;
  return {
    name: getTextValue(pageFields?.Heading),
    description: getTextValue(pageFields?.Description),
  };
}

function getPageName(route: any) {
  const pageBannerText = getPageBannerText(route);

  return stripTags(
    pageBannerText.name ||
      getFieldValue(route?.fields, "seoTitle") ||
      getFieldValue(route?.fields, "pageTitle") ||
      route?.displayName ||
      ""
  );
}

function getPageDescription(route: any) {
  const pageBannerText = getPageBannerText(route);

  return stripTags(
    pageBannerText.description ||
      getFieldValue(route?.fields, "seoDescription") ||
      getFieldValue(route?.fields, "pageDescription") ||
      ""
  );
}

function getSectionHeading(component: RouteComponent) {
  return stripTags(component?.fields?.data?.datasource?.heading?.jss?.value);
}

function getCheckboxValue(field: any) {
  const value = field?.jss?.value ?? field?.value;

  if (typeof value === "boolean") {
    return value;
  }

  return ["1", "true"].includes(String(value ?? "").trim().toLowerCase());
}

// Mirrors the card list's "Show courses only" checkbox: when it is ticked the
// component renders every selected course and never fetches events, so the
// schema must not advertise events either.
function isTrendingCoursesOnly(component: RouteComponent) {
  return getCheckboxValue(component?.fields?.data?.datasource?.showCoursesOnly);
}

// Mirrors the card list's numCards cap, which only applies when courses AND
// events are displayed together.
function getTrendingNumCards(component: RouteComponent) {
  const numCards = Number(getTextValue(component?.fields?.data?.datasource?.numCards));

  return Number.isFinite(numCards) && numCards > 0 ? numCards : DEFAULT_TRENDING_NUM_CARDS;
}

function getTopicName(topic: any, fallback = "") {
  return stripTags(
    getTextValue(topic?.displayName) ||
      getTextValue(topic?.name) ||
      getTextValue(topic?.title) ||
      getTextValue(topic?.label) ||
      getTextValue(topic?.fields?.displayName) ||
      getTextValue(topic?.fields?.name) ||
      getTextValue(topic?.fields?.title) ||
      getTextValue(topic?.fields?.label) ||
      fallback
  );
}

function interleaveItems(primaryItems: ListingItem[], secondaryItems: ListingItem[]) {
  const interleaved: ListingItem[] = [];
  const maxLength = Math.max(primaryItems.length, secondaryItems.length);

  for (let index = 0; index < maxLength; index += 1) {
    if (index < primaryItems.length) {
      interleaved.push(primaryItems[index]);
    }

    if (index < secondaryItems.length) {
      interleaved.push(secondaryItems[index]);
    }
  }

  return interleaved;
}

function dedupeListingItems(items: ListingItem[]) {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key =
      item?.id ||
      getTextValue(item?.title) ||
      item?.marketingTitle?.jss?.value ||
      item?.pageTitle?.jss?.value ||
      JSON.stringify(item?.link || item?.url || item?.href || "");

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function matchesTopic(item: ListingItem, topicId?: string) {
  if (!topicId) {
    return true;
  }

  if (item?.category && item.category.toLowerCase().replace(/[\s\W]+/g, "-") === topicId) {
    return true;
  }

  if (Array.isArray(item?.tags) && item.tags.some((tag) => tag?.id === topicId)) {
    return true;
  }

  return item?.type === topicId;
}

function filterCountryCourses(items: ListingItem[], countryName: string) {
  return items.filter((course) => {
    const reason = course?.courseReason;

    if (!reason) {
      return true;
    }

    if (Array.isArray(reason)) {
      return reason.some((entry) => entry?.includes(countryName));
    }

    return typeof reason === "string" ? reason.includes(countryName) : true;
  });
}

function deriveTopicsFromItems(items: ListingItem[]) {
  const tagsMap = new Map<string, { id: string; displayName: string }>();

  items.forEach((item) => {
    if (Array.isArray(item?.tags) && item.tags.length > 0) {
      item.tags.forEach((tag) => {
        if (tag?.id && !tagsMap.has(tag.id)) {
          tagsMap.set(tag.id, {
            id: tag.id,
            displayName: tag.displayName || tag.id,
          });
        }
      });

      return;
    }

    if (!item?.category) {
      return;
    }

    const categoryId = item.category.toLowerCase().replace(/[\s\W]+/g, "-");
    if (!tagsMap.has(categoryId)) {
      tagsMap.set(categoryId, {
        id: categoryId,
        displayName: item.category,
      });
    }
  });

  return Array.from(tagsMap.values());
}

function getCourseCardTopics(component: RouteComponent) {
  const datasource = component?.fields?.data?.datasource || {};
  const contextItem = component?.fields?.data?.contextItem || {};
  const contextChildren = contextItem?.children || [];
  const topicsFromChildren = Array.isArray(contextChildren)
    ? contextChildren
        .map((child: any) =>
          child?.topic?.targetItem
            ? {
                ...child.topic.targetItem,
                url: child.url,
              }
            : null
        )
        .filter(Boolean)
    : [];
  const contextTopics =
    topicsFromChildren.length > 0 ? topicsFromChildren : contextItem?.topics?.targetItems || [];
  const datasourceTopics = datasource?.topics?.targetItems || [];
  const existingTopics = datasourceTopics.length > 0 ? datasourceTopics : contextTopics;

  if (existingTopics.length > 0) {
    return existingTopics;
  }

  return deriveTopicsFromItems(Array.isArray(datasource?.items) ? datasource.items : []);
}

function getTrendingCourseItems(component: RouteComponent, baseUrl: string) {
  const rawItems = component?.fields?.data?.datasource?.trendingCardList?.targetItems || [];

  return (Array.isArray(rawItems) ? rawItems : []).map((item: ListingItem, index: number) => ({
    id: item?.id || `trending-course-${index}`,
    type: "contentDetail",
    title: item?.marketingTitle?.jss?.value || item?.pageTitle?.jss?.value || "",
    link: resolveLinkValue(baseUrl, item) || (item as any)?.url?.jss?.value || (item as any)?.url?.value,
    format: typeof item?.format === "string" ? item.format : item?.format?.jss?.value || "",
    startDate: item?.oStartDate?.jss?.value || "",
    cpdHours: item?.cPDhours?.jss?.value || null,
    rating: getJssOrStringValue(item?.rating) || null,
    children: item?.children,
  }));
}

function getCourseCardItems(component: RouteComponent) {
  const items = component?.fields?.data?.datasource?.items;
  return Array.isArray(items) ? items : [];
}

function getCurrentCardTopics(component: RouteComponent) {
  const topics = component?.fields?.data?.contextItem?.subTopicsDisplayed?.targetItems;
  return Array.isArray(topics) ? topics : [];
}

function getCareerStageItems(stage: any): ListingItem[] {
  const items = stage?.courses?.targetItems;

  if (!Array.isArray(items)) {
    return [];
  }

  return items.slice(0, 2).map((item: any) => ({
    id: item?.id,
    type: "course",
    title: getTextValue(item?.title),
    link: item?.link,
    format: getTextValue(item?.format),
    startDate: getTextValue(item?.startDate),
    cpdHours: getTextValue(item?.CPDhours) || null,
    rating: getTextValue(item?.rating) || null,
    // Career stage courses carry per-region pricing under `courseCountries`;
    // map it into `countryPrices` so the offers are built like other sections.
    countryPrices: (Array.isArray(item?.courseCountries) ? item.courseCountries : [])
      .flatMap((group: any) => (Array.isArray(group?.countries) ? group.countries : []))
      .map((countryPrice: any) => ({
        regionKey: countryPrice?.name,
        memberPrice: getTextValue(countryPrice?.memberPrice),
        nonMemberPrice: getTextValue(countryPrice?.nonMemberPrice),
        fSPrice: getTextValue(countryPrice?.fSPrice),
        currencyCode: getTextValue(countryPrice?.currencyCode),
      })),
  }));
}

function getCountryRegionKey(country: CountryOption) {
  return country?.id?.toLowerCase() || "";
}

function getCourseRegionPrice(item: ListingItem, country: CountryOption) {
  const countryKey = getCountryRegionKey(country);
  const countryPrices = Array.isArray(item?.countryPrices) ? item.countryPrices : [];
  const countryPrice = countryPrices.find(
    (price) => price?.regionKey?.toLowerCase() === countryKey
  );

  if (countryPrice) {
    return countryPrice;
  }

  if (countryPrices.length > 0) {
    return null;
  }

  const courseRegions = item?.children
    ?.find((child: any) => child?.name === "Course Regions")
    ?.children;
  const courseRegion = Array.isArray(courseRegions)
    ? courseRegions.find((region: any) => region?.name?.toLowerCase() === countryKey)
    : null;

  if (courseRegion) {
    return {
      memberPrice: getTextValue(courseRegion?.memberPrice),
      nonMemberPrice: getTextValue(courseRegion?.nonMemberPrice),
      fSPrice: getTextValue(courseRegion?.fSPrice),
      currencyCode: getTextValue(courseRegion?.currencyCode),
    };
  }

  if (Array.isArray(courseRegions) && courseRegions.length > 0) {
    return null;
  }

  if (item?.courseMemberPrice || item?.courseCurrencyCode || item?.coursePriceType) {
    return {
      memberPrice: item.courseMemberPrice,
      nonMemberPrice: item.courseNonMemberPrice,
      fSPrice: item.coursePriceType,
      currencyCode: item.courseCurrencyCode,
    };
  }

  return null;
}

export function buildOffers(item: ListingItem, countries: CountryOption[]): SchemaOffer[] {
  if (item?.type === "event") {
    return [];
  }

  // First pass: resolve a tax-inclusive price per region.
  const seenRegions = new Set<string>();
  const resolved = countries
    .map((country) => {
      const regionName = country?.name;
      const regionKey = normalizeCountryName(regionName);
      if (!regionName || seenRegions.has(regionKey)) {
        return null;
      }

      const price = getCourseRegionPrice(item, country);
      if (!price) {
        return null;
      }

      const priceType = (price.fSPrice || "").toLowerCase();
      const isFree = priceType.includes("free") || priceType.includes("complimentary");

      // Prefer the non-member (public) price when it is a real positive amount;
      // otherwise fall back to the member price (or 0 for free). The listing data
      // currently only carries member pricing, but this keeps the template ready
      // for when a non-member price is added to the Coveo feed.
      const rawNonMemberPrice = price.nonMemberPrice;
      const nonMemberPriceNumber = Number(rawNonMemberPrice);
      const hasValidNonMemberPrice =
        rawNonMemberPrice != null &&
        String(rawNonMemberPrice).trim() !== "" &&
        Number.isFinite(nonMemberPriceNumber) &&
        nonMemberPriceNumber > 0;

      const basePrice = hasValidNonMemberPrice
        ? rawNonMemberPrice
        : price.memberPrice || (isFree ? "0" : "");

      if (!basePrice && !price.currencyCode) {
        return null;
      }

      // Make prices tax-inclusive (e.g. AU GST) to match the course detail pages.
      const taxRule = TAX_RULES[getCountryRegionKey(country)] || { rate: 0 };
      const schemaPrice = basePrice ? calcTaxInclusivePriceSafe(basePrice, taxRule.rate) : null;

      seenRegions.add(regionKey);
      return { regionName, schemaPrice, currencyCode: price.currencyCode };
    })
    .filter(Boolean) as Array<{ regionName: string; schemaPrice: string | null; currencyCode?: string }>;

  // A course that is priced in at least one region is "paid"; for those, a 0/blank
  // price means the course is not available in that region, so drop it (avoids
  // advertising misleading $0 offers for unavailable regions). A course that is
  // free everywhere (no positive price anywhere) keeps its $0 offers.
  const isPaidCourse = resolved.some((entry) => Number(entry.schemaPrice) > 0);

  return resolved
    .filter((entry) => !isPaidCourse || Number(entry.schemaPrice) > 0)
    .map((entry) => ({
      "@type": "Offer" as const,
      ...(entry.schemaPrice ? { price: entry.schemaPrice } : {}),
      ...(entry.currencyCode ? { priceCurrency: entry.currencyCode } : {}),
      eligibleRegion: { "@type": "Country" as const, name: entry.regionName },
    }));
}

function toSchemaItemList(
  name: string,
  items: ListingItem[],
  baseUrl: string,
  country: CountryOption,
  countries: CountryOption[]
) {
  const itemListElement = items
    .slice(0, MAX_SCHEMA_ITEM_LIST_ITEMS)
    .map((item, index) => {
      const title =
        getTextValue(item?.title) ||
        item?.marketingTitle?.jss?.value ||
        item?.pageTitle?.jss?.value ||
        "";
      if (!title) {
        return null;
      }

      const schemaItem: {
        "@type": "Course" | "Event";
        name: string;
        url?: string;
        startDate?: string;
        location?: ItemLocation;
        organizer?: { "@id": string };
        offers?: SchemaOffer[];
      } = {
        "@type": item?.type === "event" ? "Event" : "Course",
        name: title,
      };

      const url = resolveLinkValue(baseUrl, item);
      if (url) {
        schemaItem.url = url;
      }

      const location = buildItemLocation(country, item);
      if (location) {
        schemaItem.location = location;
      }

      if (item?.type === "event") {
        const startDate = toSchemaStartDate(item.schemaStartDate || item.startDate);
        if (startDate) {
          schemaItem.startDate = startDate;
        }

        schemaItem.organizer = {
          "@id": CPA_ORGANIZATION_ID,
        };
      }

      const offers = buildOffers(item, countries);
      if (offers.length > 0) {
        schemaItem.offers = offers;
      }

      return {
        "@type": "ListItem" as const,
        position: index + 1,
        item: schemaItem,
      };
    })
    .filter(Boolean) as ItemListEntity["itemListElement"];

  if (!name || itemListElement.length === 0) {
    return null;
  }

  return {
    "@type": "ItemList" as const,
    name,
    itemListElement,
  };
}

function toSchemaComponentSection(name: string, sections: ItemListEntity[]) {
  if (!name || sections.length === 0) {
    return null;
  }

  return {
    "@type": "WebPageElement" as const,
    name,
    mainEntity: sections,
  };
}

function buildStaticTrendingSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  const sectionHeading = getSectionHeading(component) || "Trending courses and events";
  const list = toSchemaItemList(sectionHeading, getTrendingCourseItems(component, baseUrl), baseUrl, country, countries);

  return list ? [list] : [];
}

function buildStaticCourseSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  const topics = getCourseCardTopics(component);
  const sectionHeading = getSectionHeading(component) || "Courses and events";
  const countryItems = filterCountryCourses(getCourseCardItems(component), country.name);

  if (topics.length > 0) {
    const topicSections = topics
      .map((topic: any) => {
        const topicName = getTopicName(topic, sectionHeading);
        const topicItems = countryItems.filter((item) => matchesTopic(item, topic?.id));

        return toSchemaItemList(topicName, topicItems, baseUrl, country, countries);
      })
      .filter(Boolean) as ItemListEntity[];
    const section = toSchemaComponentSection(sectionHeading, topicSections);

    return section ? [section] : [];
  }

  const list = toSchemaItemList(sectionHeading, dedupeListingItems(countryItems), baseUrl, country, countries);

  return list ? [list] : [];
}

function buildCurrentCardSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  const topics = getCurrentCardTopics(component);
  const contextItem = component?.fields?.data?.contextItem;
  const topicName = getTopicName(contextItem?.topic?.targetItem) || getTopicName(contextItem?.topic);
  const sectionHeading =
    getSectionHeading(component) || (topicName ? `Explore topics in ${topicName}` : "Explore topics");
  const topicSections = topics
    .map((topic: any) => {
      const currentTopicName = getTopicName(topic);
      const topicItems = filterCountryCourses(
        Array.isArray(topic?.courses) ? topic.courses : [],
        country.name
      );

      return toSchemaItemList(currentTopicName, topicItems, baseUrl, country, countries);
    })
    .filter(Boolean) as ItemListEntity[];
  const section = toSchemaComponentSection(sectionHeading, topicSections);

  return section ? [section] : [];
}

function buildCareerStageSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  const stages = component?.fields?.data?.datasource?.careerStages;

  if (!Array.isArray(stages)) {
    return [];
  }

  const sectionHeading = getSectionHeading(component) || "Career stage learning";
  const stageSections = stages
    .map((stage: any) => {
      const stageName = stripTags(getTextValue(stage?.pillTabText));
      return toSchemaItemList(stageName, getCareerStageItems(stage), baseUrl, country, countries);
    })
    .filter(Boolean) as ItemListEntity[];
  const section = toSchemaComponentSection(sectionHeading, stageSections);

  return section ? [section] : [];
}

function buildTrendingSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string,
  australiaEventItems: ListingItem[]
): ComponentEntity[] {
  // "Show courses only" hides events on the page - fall back to the courses-only
  // section so the schema matches what is rendered.
  if (isTrendingCoursesOnly(component)) {
    return buildStaticTrendingSections(component, country, countries, baseUrl);
  }

  const staticCourseItems = getTrendingCourseItems(component, baseUrl);
  const eventItems = isAustraliaCountry(country) ? australiaEventItems : [];
  const sectionHeading = getSectionHeading(component) || "Trending courses and events";
  const list = toSchemaItemList(
    sectionHeading,
    interleaveItems(staticCourseItems, eventItems).slice(0, getTrendingNumCards(component)),
    baseUrl,
    country,
    countries
  );

  return list ? [list] : [];
}

function getFallbackCourseItemsForTopic(component: RouteComponent, country: CountryOption, topic: any) {
  const countryItems = filterCountryCourses(getCourseCardItems(component), country.name);
  const topicItems = countryItems.filter((item) => matchesTopic(item, topic?.id));

  return topicItems.length > 0 ? topicItems : countryItems;
}

async function buildCourseSections(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): Promise<ComponentEntity[]> {
  const topics = getCourseCardTopics(component);

  if (topics.length === 0) {
    return buildStaticCourseSections(component, country, countries, baseUrl);
  }

  const sectionHeading = getSectionHeading(component) || "Courses and events";
  const datasource = component?.dataSource || "";
  const contextItemId = component?.fields?.data?.contextItem?.id || component?.uid || "";
  const topicSections = await Promise.all(
    topics.map(async (topic: any) => {
      const topicName = getTopicName(topic, sectionHeading);
      let courseItems: ListingItem[] = [];
      if (datasource && topic?.id) {
        const response = await fetchCourses(topic.id, datasource, contextItemId);
        courseItems = filterCountryCourses(response?.items || [], country.name);
      }

      if (courseItems.length === 0) {
        courseItems = getFallbackCourseItemsForTopic(component, country, topic);
      }

      return toSchemaItemList(topicName, courseItems, baseUrl, country, countries);
    })
  );
  const section = toSchemaComponentSection(
    sectionHeading,
    topicSections.filter(Boolean) as ItemListEntity[]
  );

  return section ? [section] : [];
}

function buildSectionsForComponent(
  component: RouteComponent,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  if (component.componentName === "TrendingCardList") {
    return buildStaticTrendingSections(component, country, countries, baseUrl);
  }

  if (component.componentName === "CourseCardList") {
    return buildStaticCourseSections(component, country, countries, baseUrl);
  }

  if (component.componentName === "CurrentCardContainer") {
    return buildCurrentCardSections(component, country, countries, baseUrl);
  }

  if (component.componentName === "CareerStageLearning") {
    return buildCareerStageSections(component, country, countries, baseUrl);
  }

  return [];
}

function buildStaticSections(
  route: any,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): ComponentEntity[] {
  const listingComponents = collectComponents(route, LISTING_COMPONENT_NAMES);

  return listingComponents.flatMap<ComponentEntity>((component) =>
    buildSectionsForComponent(component, country, countries, baseUrl)
  );
}

async function fetchAustraliaTrendingEvents() {
  try {
    const eventItems = await fetchCoveoEvents(undefined, undefined, AUSTRALIA_COUNTRY.name, {
      numberOfResults: 5,
      sortCriteria: "@cventstartdateutc ascending",
      cpdEventsOnly: true
    });
    return eventItems.map((item) => ({
      ...item,
      schemaCountryName: AUSTRALIA_COUNTRY.name,
    }));
  } catch {
    return [];
  }
}

async function buildDynamicSections(
  route: any,
  country: CountryOption,
  countries: CountryOption[],
  baseUrl: string
): Promise<ComponentEntity[]> {
  const listingComponents = collectComponents(route, LISTING_COMPONENT_NAMES);
  const shouldFetchTrendingEvents = listingComponents.some(
    (component) => component.componentName === "TrendingCardList" && !isTrendingCoursesOnly(component)
  );
  const australiaTrendingEvents = shouldFetchTrendingEvents ? await fetchAustraliaTrendingEvents() : [];

  const groupedSections = await Promise.all(
    listingComponents.map(async (component) => {
      try {
        if (component.componentName === "TrendingCardList") {
          return buildTrendingSections(component, country, countries, baseUrl, australiaTrendingEvents);
        }

        if (component.componentName === "CourseCardList") {
          return await buildCourseSections(component, country, countries, baseUrl);
        }

        if (component.componentName === "CurrentCardContainer") {
          return buildCurrentCardSections(component, country, countries, baseUrl);
        }

        if (component.componentName === "CareerStageLearning") {
          return buildCareerStageSections(component, country, countries, baseUrl);
        }
      } catch {
        return buildSectionsForComponent(component, country, countries, baseUrl);
      }

      return [];
    })
  );

  return groupedSections.flat();
}

function buildCollectionPageSchema(context: any, route: any, sections: ComponentEntity[]) {
  const pageName = getPageName(route);
  const pageDescription = getPageDescription(route);
  const pageUrl = context?.canonicalUrl ?? "";

  if (!pageName || !pageUrl || !sections || sections.length === 0) {
    return null;
  }

  const schemaData: WithContext<any> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageName,
    url: pageUrl,
    mainEntity: sections,
  };

  if (pageDescription) {
    schemaData.description = pageDescription;
  }

  return schemaData;
}

function canBuildTopicCategorySchema(context: any, rendering: any) {
  const siteName = context?.site?.name;
  const templateId = rendering?.templateId?.toLowerCase();
  const routeName = normalizeCountryName(rendering?.name);
  const itemPath = normalizeCountryName(context?.itemPath);
  const isCourseTopicPage = templateId === COURSE_TOPIC_PAGE_TEMPLATE_ID;
  const isProfessionalDevelopmentLanding =
    routeName === "professional-development" && itemPath.includes("professional-development");
  const listingComponents = collectComponents(rendering, LISTING_COMPONENT_NAMES);
  const countrySelectorComponents = collectComponents(rendering, COUNTRY_COMPONENT_NAMES);

  return (
    !!siteName &&
    (isCourseTopicPage || isProfessionalDevelopmentLanding) &&
    listingComponents.length > 0 &&
    countrySelectorComponents.length > 0
  );
}

export function buildStaticTopicCategoryStructuredData(context: any, rendering: any) {
  if (!canBuildTopicCategorySchema(context, rendering)) {
    return null;
  }

  const canonicalUrl = context?.canonicalUrl ?? "";
  const schemaContext = { canonicalUrl };
  const countries = getCountryOptions(rendering);
  const defaultCountry = getDefaultCountry(countries);
  const baseUrl = getSiteBaseUrl(context);

  return buildCollectionPageSchema(
    schemaContext,
    rendering,
    buildStaticSections(rendering, defaultCountry, countries, baseUrl)
  );
}

export async function buildTopicCategoryStructuredData(context: any, rendering: any) {
  if (!canBuildTopicCategorySchema(context, rendering)) {
    return null;
  }

  const canonicalUrl = context?.canonicalUrl ?? "";
  const schemaContext = { canonicalUrl };
  const countries = getCountryOptions(rendering);
  const defaultCountry = getDefaultCountry(countries);
  const baseUrl = getSiteBaseUrl(context);
  const sections = await buildDynamicSections(rendering, defaultCountry, countries, baseUrl);

  return buildCollectionPageSchema(schemaContext, rendering, sections);
}

export const buildTopicCategoryStructureData = buildTopicCategoryStructuredData;

export const TopicCategoryStructureData: React.FC<any> = ({ context, rendering }) => {
  const siteName = context?.site?.name;
  const canonicalUrl = context?.canonicalUrl ?? "";
  const schemaContext = useMemo(() => ({ canonicalUrl }), [canonicalUrl]);
  const listingComponents = useMemo(
    () => collectComponents(rendering, LISTING_COMPONENT_NAMES),
    [rendering]
  );
  const countrySelectorComponents = useMemo(
    () => collectComponents(rendering, COUNTRY_COMPONENT_NAMES),
    [rendering]
  );
  const isTopicCategoryPage = listingComponents.length > 0 && countrySelectorComponents.length > 0;
  const countries = useMemo(() => getCountryOptions(rendering), [rendering]);
  const defaultCountry = useMemo(() => getDefaultCountry(countries), [countries]);
  const baseUrl = useMemo(
    () => getSiteBaseUrl(context),
    [context?.canonicalUrl, context?.siteInfo?.siteBaseUrl]
  );
  const staticSchemaData = useMemo(() => {
    if (!siteName || !isTopicCategoryPage) {
      return null;
    }

    return buildCollectionPageSchema(
      schemaContext,
      rendering,
      buildStaticSections(rendering, defaultCountry, countries, baseUrl)
    );
  }, [
    baseUrl,
    countries,
    defaultCountry,
    isTopicCategoryPage,
    rendering,
    schemaContext,
    siteName,
  ]);
  const [schemaData, setSchemaData] = useState<WithContext<any> | null>(staticSchemaData);

  useEffect(() => {
    setSchemaData(staticSchemaData);
  }, [staticSchemaData]);

  useEffect(() => {
    let isCancelled = false;

    if (!siteName || !isTopicCategoryPage) {
      setSchemaData(null);
      return () => {
        isCancelled = true;
      };
    }

    const loadStructuredData = async () => {
      const nextSections = await buildDynamicSections(rendering, defaultCountry, countries, baseUrl);
      if (isCancelled) {
        return;
      }

      const nextSchemaData = buildCollectionPageSchema(
        schemaContext,
        rendering,
        nextSections
      );

      if (nextSchemaData) {
        setSchemaData(nextSchemaData);
      }
    };

    loadStructuredData();

    return () => {
      isCancelled = true;
    };
  }, [
    baseUrl,
    countries,
    defaultCountry,
    isTopicCategoryPage,
    rendering,
    schemaContext,
    siteName,
  ]);

  if (!schemaData) {
    return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>
  );
};
